import type { DateValue } from "@internationalized/date";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { CreatorMetrics } from "@unbox/api";
import {
  Announcement03,
  FilePlus02,
  InfoCircle,
  MessageTextSquare02,
  // MinusCircle, // Commented out - Remove from Collab feature pending Samples API integration
  SlashCircle01,
  UserSquare,
  Users01,
} from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import {
  type CreatorMetricsSortColumn,
  useCreatorMetricRowsFetcher,
  useCreatorMetricsDownload,
  useCreatorsCount,
  useCreatorsMetrics,
} from "@/api/creator-metrics";
import { usePagination } from "@/api/pagination";
import { useMerchantRecommendationsCount, useMerchantRecommendationsDownload } from "@/api/recommendations";
import { useRules } from "@/api/rules";
import { SelectAllPagesButton } from "@/components/application/table/select-all-pages-button";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { BulkCohortButton } from "@/components/creator-cohorts/bulk-cohort-button";
import { BulkTagButton } from "@/components/creator-tags/bulk-tag-button";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { FilterPill, FilterPills } from "@/components/filters/filter-pills";
import { CreatorFilterPanel } from "@/components/filters/panels";
import { SearchAndFilterActions } from "@/components/filters/search-and-filter-actions";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { PageLayout } from "@/components/page-layout";
import { VideoDetails } from "@/components/video-details";
import { generateFilterSyncConfig, useAutoFilterUrlSync } from "@/hooks/use-auto-filter-sync";
import { useCache } from "@/hooks/use-cache";
import { useCrossPageSelection } from "@/hooks/use-cross-page-selection";
import { useDateRangeUrlSync } from "@/hooks/use-date-range-url-sync";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { useExplorerDateFilter } from "@/hooks/use-explorer-date-filter";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useIsAssurance } from "@/hooks/use-is-assurance";
import { useSearchUrlSync } from "@/hooks/use-search-url-sync";
import { useSelectAllAcrossPages } from "@/hooks/use-select-all-across-pages";
import { useSort } from "@/hooks/use-sort";
import { useTrackedBlockCreators } from "@/hooks/use-tracked-block-creators";
import { useUrlFilterSync } from "@/hooks/use-url-filter-sync";
import { CohortLibraryModal } from "@/pages/creator-cohorts/cohort-library-modal";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { DeleteCreatorListModal } from "@/pages/creator-lists/delete-modal";
import { EditCreatorListModal } from "@/pages/creator-lists/edit-modal";
import { RemoveFromCreatorList } from "@/pages/creator-lists/remove-from-creator-lists";
import { TagLibraryModal } from "@/pages/creator-tags/tag-library-modal";
import { CreateCreatorList } from "@/pages/creators/brand-creators/create-creator-list";
import { CreatorDetails } from "@/pages/creators/creator-details";
import { CreatorClassificationTabs, getClassificationFromTab } from "@/pages/explorers/creator-classification-tabs";
import { CreatorExplorerTable } from "@/pages/explorers/creator-explorer-table";
import { downloadRecommendationsCSV } from "@/pages/explorers/recommendations-csv";
import { toRecommendationsFilters } from "@/pages/explorers/recommendations-filters";
import { RecommendationsTable } from "@/pages/explorers/recommendations-table";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import type { creatorExplorerRoute } from "@/router";
import { downloadCSV } from "@/utils/download-csv";
import { compactNumberFormatter } from "@/utils/formatNumber";

const transformCreatorData = (data: { creators: CreatorMetrics[] }) => {
  return data.creators.map((creator) => ({
    ...creator,
    videosChange: creator.trendVideos ? (creator.videos - creator.trendVideos) / creator.trendVideos : null,
    unitSalesChange: creator.trendUnitSales
      ? (creator.unitSales - creator.trendUnitSales) / creator.trendUnitSales
      : null,
    gmvChange: creator.trendGmv ? (creator.gmv - creator.trendGmv) / creator.trendGmv : null,
  }));
};

export type TransformedCreator = CreatorMetrics & {
  videosChange: number | null;
  unitSalesChange: number | null;
  gmvChange: number | null;
};

type CreatorData = {
  id: string;
  creatorId: string;
  tiktokOpenId: string | null;
  tagOpenId: string | null;
  handle: string | null;
  thumbnail: string | null;
  creatorName: string | null;
  nickname: string | null;
};

const CLASSIFICATION_DESCRIPTIONS: Record<string, React.ReactNode> = {
  recommended_creators: (
    <>
      <p className="font-semibold">Top Performer</p>
      <span>Must meet ≥3 of 4 criteria:</span>
      <ul className="list-disc pl-4">
        <li>Top 10% GMV (revenue)</li>
        <li>Top 20% Posts (posting frequency)</li>
        <li>Top 30% GMV/Post (revenue per post)</li>
        <li>Top 30% GPM (revenue per 1000 views)</li>
      </ul>
    </>
  ),
  top_creator: (
    <>
      <p className="font-semibold">Top Creator</p>
      <span>Must meet ≥2 of the following criteria:</span>
      <ul className="list-disc pl-4">
        <li>Top 10% GMV in the last week</li>
        <li>Top 30% GMV per post in the last week</li>
        <li>Top 30% GPM (revenue per 1K views) in the last week</li>
      </ul>
    </>
  ),
  rising_star: (
    <>
      <p className="font-semibold">Rising Star</p>
      <span>Must meet the following criteria:</span>
      <ul className="list-disc pl-4">
        <li>Started generating GMV in last 7 days</li>
        <li>OR high GMV week-over-week growth</li>
      </ul>
    </>
  ),
  active_partner: (
    <>
      <p className="font-semibold">Active Partner</p>
      <span>Must meet the following criteria:</span>
      <ul className="list-disc pl-4">
        <li>Creator who has posted in the last 30 days</li>
        <li>Not a Top Creator</li>
      </ul>
    </>
  ),
  dormant_creator: (
    <>
      <p className="font-semibold">Dormant Creator</p>
      <span>Must meet the following criteria:</span>
      <ul className="list-disc pl-4">
        <li>No posts in last 30 days (but posted previously)</li>
        <li>Not a top creator</li>
      </ul>
    </>
  ),
  safe_creator: (
    <>
      <p className="font-semibold">Safer Creator</p>
      <span>Must meet the following criteria:</span>
      <ul className="list-disc pl-4">
        <li>Zero Tier 1 (high-risk) flags in the last week</li>
        <li>Has posted OR generated GMV in the last week</li>
      </ul>
    </>
  ),
};

const CreatorExplorerContent = () => {
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof creatorExplorerRoute)["useSearch"]>;
  const {
    creator_id,
    startDate,
    endDate,
    search: urlSearch,
    classification: urlClassification,
    ruleArea,
    ruleId,
  } = urlParams;
  const navigate = useNavigate() as ReturnType<(typeof creatorExplorerRoute)["useNavigate"]>;
  const { dateRange, isApplied, clear: clearDateRange, changeDate } = useExplorerDateFilter();
  const creatorTagsEnabled = useFeatureFlag("F-creator-tags");
  const [isCreatingList, setIsCreatingList] = useState(false);
  const selectedList = isCreatingList ? "__create-new" : (urlParams.listId ?? "__all");
  const selectedClassification =
    urlParams.listId || isCreatingList ? "" : (urlClassification ?? "recommended_creators");
  const isRecommendationsTab = selectedClassification === "recommended_creators";
  const [tagLibraryOpen, setTagLibraryOpen] = useState(false);
  const [cohortLibraryOpen, setCohortLibraryOpen] = useState(false);

  const { data: recommendationsCount } = useMerchantRecommendationsCount({
    isCreator: true,
    enabled: isRecommendationsTab,
  });
  const [classificationTooltipOpen, setClassificationTooltipOpen] = useState(false);
  const { form, apiFilters, reset, activeFields, hasActiveFilters: hasActiveFilterFields } = useFilters();
  const [search, setSearch] = useState(urlSearch || "");
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | undefined>(creator_id);
  const [selectedVideoId, setSelectedVideoId] = useState<string>();
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [bulkInviteModalOpen, setBulkInviteModalOpen] = useState(false);
  // const [bulkRemoveModalOpen, setBulkRemoveModalOpen] = useState(false); // Commented out - Remove from Collab feature pending Samples API integration
  const [addToListsBulkOpen, setAddToListsBulkOpen] = useState(false);
  const blockCreators = useTrackedBlockCreators();
  const { value: debouncedSearch, clear: clearSearch } = useClearableDebounce(search);

  const classificationFilter = getClassificationFromTab(selectedClassification);
  // Automatically sync URL params to form based on active fields
  useAutoFilterUrlSync(form, urlParams, activeFields);

  // Generate field mapping and currentSearch dynamically
  const { fieldMapping, currentSearch } = useMemo(
    () => generateFilterSyncConfig(activeFields, urlParams),
    [activeFields, urlParams],
  );

  // Sync form changes back to URL
  useUrlFilterSync({
    form,
    fieldMapping,
    navigate,
    currentSearch,
  });

  // Sync date range to URL
  useDateRangeUrlSync({
    dateRange,
    changeDate,
    navigate,
    currentSearch: { startDate, endDate },
    isApplied,
  });

  // Sync search to URL
  useSearchUrlSync({
    search,
    setSearch,
    navigate,
    currentSearch: { search: urlSearch },
  });

  const creatorListFilter = ["__all", "__create-new"].includes(selectedList) ? undefined : selectedList;
  const filters = useMemo(
    () => ({
      ...apiFilters,
      text: debouncedSearch,
      creatorList: creatorListFilter,
      classification: classificationFilter,
      ruleArea,
      ruleId,
    }),
    [apiFilters, debouncedSearch, creatorListFilter, classificationFilter, ruleArea, ruleId],
  );

  const { data: rulesForFilter } = useRules({ enabled: !!ruleId });
  const filteringRule = ruleId ? rulesForFilter?.items.find((rule) => rule.ruleId === ruleId) : undefined;
  const clearRuleFilter = () => navigate({ search: (prev) => ({ ...prev, ruleArea: undefined, ruleId: undefined }) });
  const rulePill = ruleId ? (
    <FilterPill label="Rule" value={filteringRule?.name ?? "…"} onRemove={clearRuleFilter} />
  ) : undefined;
  const trimmedSearch = search.trim();
  const searchPill = trimmedSearch ? (
    <FilterPill label="Search" value={trimmedSearch} onRemove={() => setSearch("")} />
  ) : undefined;
  const tablePills =
    searchPill || rulePill ? (
      <>
        {searchPill}
        {rulePill}
      </>
    ) : undefined;

  const recommendationsFilters = useMemo(
    () => toRecommendationsFilters(apiFilters, debouncedSearch),
    [apiFilters, debouncedSearch],
  );

  const isAssurance = useIsAssurance();

  const pagination = usePagination();
  const { sort, setFromDescriptor, descriptor } = useSort<CreatorMetricsSortColumn>("gmv");
  const sortByName = useCallback(
    () => setFromDescriptor({ column: "creatorName", direction: "ascending" }),
    [setFromDescriptor],
  );

  const { data } = useCreatorsMetrics({
    range: dateRange,
    pagination: pagination.pagination,
    sort,
    enabled: !isRecommendationsTab,
    ...filters,
  });
  const { data: count } = useCreatorsCount({ range: dateRange, enabled: !isRecommendationsTab, ...filters });
  const total = useCache(count?.totalCreators);

  const [filtersOpen, setFiltersOpen] = useState(false);

  const tableData = useMemo(() => {
    if (!data) return undefined;
    return transformCreatorData(data);
  }, [data]);

  const {
    selectedKeys: selected,
    selectedData: selectedCreatorsData,
    isAllPagesSelected,
    selectedCount,
    clear: clearSelection,
    selectAllAcrossPages,
    createSelectionHandler,
  } = useCrossPageSelection<TransformedCreator, CreatorData>({
    getKey: useCallback((c: TransformedCreator) => c.creatorId, []),
    toData: useCallback(
      (c: TransformedCreator) => ({
        id: c.creatorId,
        creatorId: c.creatorId,
        tiktokOpenId: c.tiktokCreatorOpenId,
        tagOpenId: c.tagOpenId,
        handle: c.creatorName,
        thumbnail: c.thumbnail,
        creatorName: c.creatorName,
        nickname: c.nickname,
      }),
      [],
    ),
  });

  const handleSelectionChange = useMemo(() => createSelectionHandler(tableData), [createSelectionHandler, tableData]);

  const fetchMetricRows = useCreatorMetricRowsFetcher({ range: dateRange, ...filters });
  const fetchChunk = useCallback(
    async (offset: number, limit: number) => {
      const chunk = await fetchMetricRows(offset, limit);
      return { items: transformCreatorData({ creators: chunk.items }), total: chunk.total };
    },
    [fetchMetricRows],
  );
  const selectAll = useSelectAllAcrossPages({
    fetchChunk,
    total: total ?? 0,
    loadedCount: tableData?.length ?? 0,
    selection: { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection },
    resetKey: JSON.stringify({ dateRange, filters }),
  });
  const handleClearSelection = selectAll.clear;

  useEffect(() => {
    const subscription = form.watch(() => pagination.setPage(1));
    return subscription.unsubscribe;
  }, [form, pagination.setPage]);

  const { data: cohorts } = useCreatorCohorts();
  const cohortNameById = useMemo(() => new Map((cohorts ?? []).map((c) => [c.id, c.name])), [cohorts]);

  const { mutateAsync: fetchRecommendations, isPending: isDownloadingRecommendations } =
    useMerchantRecommendationsDownload();
  const downloadRecommendations = async () => {
    const rows = await fetchRecommendations({ isCreator: true, filters: recommendationsFilters });
    downloadRecommendationsCSV(rows, "best_matches", cohortNameById);
  };

  const { mutateAsync, isPending: isDownloading } = useCreatorMetricsDownload();
  const downloadTableDate = async () => {
    if (!total) return;
    const rows = await mutateAsync({
      total,
      range: dateRange,
      sort,
      ...filters,
    });
    const assuranceCols: (keyof CreatorMetrics)[] = isAssurance
      ? ["flaggedVideos", "riskTiers", "actionCount", "tier2ActionCount"]
      : [];
    const assuranceHeaders = isAssurance ? ["Flagged Videos", "Risk Tiers", "Actions", "T2 Strikes"] : [];
    downloadCSV(
      rows.map((r) => ({
        ...r,
        creatorId: r.creatorId.replace("CID-", ""),
        cohort: r.cohortId ? (cohortNameById.get(r.cohortId) ?? "") : "",
        // Flatten demographics for CSV
        topGender: r.topGender ? `${r.topGender.gender} (${r.topGender.percentage}%)` : "",
        topAgeRange: r.topAgeRange ? `${r.topAgeRange.range} (${r.topAgeRange.percentage}%)` : "",
      })),
      "creator_explorer",
      [
        "creatorId",
        "creatorName",
        "cohort",
        "matchQualityScore",
        "gmv",
        "topGender",
        "topAgeRange",
        "videos",
        "followerCount",
        ...assuranceCols,
      ],
      [
        "Creator ID",
        "Creator Name",
        "Cohort",
        "Match Quality",
        "GMV",
        "Gender Demo",
        "Main Age Demo",
        "Videos",
        "Followers",
        ...assuranceHeaders,
      ],
    );
  };

  const [, setTempDateRange] = useState<{ start: DateValue; end: DateValue } | null>(dateRange);
  useEffect(() => {
    setTempDateRange(dateRange);
  }, [dateRange]);

  const resetFilters = () => {
    clearDateRange();
    setSearch("");
    clearSearch();
    reset();
  };

  useEffect(() => {
    if (isCreatingList) {
      resetFilters();
      setFiltersOpen(false);
    }
  }, [isCreatingList]);

  const isInCreatorList = !["__all", "__create-new"].includes(selectedList);

  const selectClassification = (tab: string) => {
    if (isInCreatorList) resetFilters();
    setIsCreatingList(false);
    handleClearSelection();
    navigate({
      search: (prev) => ({
        ...prev,
        classification: tab as (typeof urlParams)["classification"],
        listId: undefined,
        ruleArea: undefined,
        ruleId: undefined,
      }),
    });
  };

  const selectList = (list: string) => {
    if (list === "__create-new") {
      if (ruleId) clearRuleFilter();
      setIsCreatingList(true);
      return;
    }
    setIsCreatingList(false);
    if (!["__all", "__create-new"].includes(list)) resetFilters();
    handleClearSelection();
    navigate({
      search: (prev) => ({
        ...prev,
        listId: list === "__all" ? undefined : list,
        classification: undefined,
        ...(list !== "__all" && { ruleArea: undefined, ruleId: undefined }),
      }),
    });
  };

  useEffect(() => {
    setClassificationTooltipOpen(false);
  }, [selectedClassification]);

  return (
    <PageLayout
      drawerOpen={filtersOpen}
      title="Your Creators"
      icon={Users01}
      subtitle="Manage and analyze your creator network"
      callToAction={
        selectedList !== "__create-new" && (
          <div className="flex flex-row items-center gap-2">
            {isInCreatorList && (
              <>
                <EditCreatorListModal id={selectedList} />
                <DeleteCreatorListModal id={selectedList} onDelete={() => selectList("__all")} />
              </>
            )}
            <ExportCsvButton
              onClick={isRecommendationsTab ? downloadRecommendations : downloadTableDate}
              isDownloading={isRecommendationsTab ? isDownloadingRecommendations : isDownloading}
            />
            {isRecommendationsTab && (
              <Button
                color="primary"
                iconLeading={UserSquare}
                isDisabled={!recommendationsCount?.hasUnactionedRecommendations}
                onClick={() =>
                  navigate({ to: "/merchant/$merchantId/review-recommendations", search: { type: "creator" } })
                }
              >
                Review Matches
              </Button>
            )}

            <TagLibraryModal open={tagLibraryOpen} onOpenChange={setTagLibraryOpen} />
            <CohortLibraryModal open={cohortLibraryOpen} onOpenChange={setCohortLibraryOpen} />
          </div>
        )
      }
    >
      <div className="flex flex-col gap-5 w-full flex-1 min-h-0">
        <div className="flex flex-row items-end -mb-2">
          <CreatorClassificationTabs
            selected={selectedClassification}
            setSelected={selectClassification}
            selectedList={selectedList}
            setSelectedList={selectList}
            showCustomLists
            showRecommendations
          />
        </div>
        {selectedList === "__create-new" ? (
          <CreateCreatorList onCreate={(id) => selectList(id)} />
        ) : isRecommendationsTab ? (
          <>
            <RecommendationsTable
              isCreator={true}
              filters={recommendationsFilters}
              filterActions={
                <SearchAndFilterActions
                  search={search}
                  onSearchChange={setSearch}
                  onToggleFilters={() => setFiltersOpen((prev) => !prev)}
                />
              }
              filterSection={<FilterPills className="-my-2" extraPills={searchPill} />}
              onCreatorSelect={(creatorId) => {
                setSelectedCreatorId(creatorId);
                navigate({ search: (prev) => ({ ...prev, creator_id: creatorId }) });
              }}
            />
            <CreatorDetails
              id={selectedCreatorId}
              onClose={() => {
                setSelectedCreatorId(undefined);
                navigate({ search: (prev) => ({ ...prev, creator_id: undefined }) });
              }}
            />
          </>
        ) : (
          <>
            {selected.length === 0 ? (
              <div className="flex flex-row items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-1.5 text-sm font-medium">
                  {count ? `${compactNumberFormatter.format(count.totalCreators)} Creators` : "Loading..."}
                  {selectedClassification !== "" && CLASSIFICATION_DESCRIPTIONS[selectedClassification] && (
                    <Tooltip
                      title={CLASSIFICATION_DESCRIPTIONS[selectedClassification]}
                      isOpen={classificationTooltipOpen}
                      onOpenChange={setClassificationTooltipOpen}
                    >
                      <TooltipTrigger onPress={() => setClassificationTooltipOpen((prev) => !prev)}>
                        <InfoCircle className="w-4 h-4 text-tertiary" />
                      </TooltipTrigger>
                    </Tooltip>
                  )}
                </div>
                <SearchAndFilterActions
                  search={search}
                  onSearchChange={setSearch}
                  onToggleFilters={() => setFiltersOpen((prev) => !prev)}
                />
              </div>
            ) : (
              <div className="flex flex-row justify-between items-center rounded-lg bg-brand-primary -my-2 p-2">
                <div className="flex flex-row items-center gap-2">
                  <CloseButton onClick={handleClearSelection} />
                  <span className="text-sm font-medium">{selectedCount.toLocaleString()} selected</span>
                  <SelectAllPagesButton state={selectAll} />
                </div>
                <div className="flex flex-row gap-2 items-center ">
                  <Button
                    iconLeading={Announcement03}
                    onClick={() => setBulkInviteModalOpen(true)}
                    isDisabled={selectAll.isFetching}
                  >
                    Invite to Collab
                  </Button>
                  {/* Remove from Collab feature commented out - pending Samples API integration
                      <Button iconLeading={MinusCircle} onClick={() => setBulkRemoveModalOpen(true)}>
                        Remove from Collab
                      </Button>
                      */}
                  <Button
                    iconLeading={MessageTextSquare02}
                    onClick={() => setMessageDialogOpen(true)}
                    isDisabled={selectAll.isFetching}
                  >
                    Message
                  </Button>
                  <MessageCreatorsDialog
                    recipients={Array.from(selectedCreatorsData.values())}
                    open={messageDialogOpen}
                    onOpenChange={setMessageDialogOpen}
                    onSuccess={handleClearSelection}
                  />
                  {isInCreatorList && (
                    <RemoveFromCreatorList selected={selected} list={selectedList} onRemove={handleClearSelection} />
                  )}
                  {creatorTagsEnabled ? (
                    <BulkTagButton
                      creators={[...selected].map((id) => ({
                        tiktokCreatorOpenId: selectedCreatorsData.get(id)?.tagOpenId ?? null,
                        label: selectedCreatorsData.get(id)?.handle ?? undefined,
                      }))}
                      onApplied={handleClearSelection}
                      isDisabled={selectAll.isFetching}
                    />
                  ) : (
                    <Button
                      iconLeading={FilePlus02}
                      onClick={() => setAddToListsBulkOpen(true)}
                      isDisabled={selectAll.isFetching}
                    >
                      Add to list
                    </Button>
                  )}
                  <BulkCohortButton
                    creatorIds={[...selected]}
                    onApplied={handleClearSelection}
                    isDisabled={selectAll.isFetching}
                    getCreatorLabel={(id) => selectedCreatorsData.get(id)?.handle ?? undefined}
                  />
                  <Button
                    iconLeading={SlashCircle01}
                    isDisabled={selectAll.isFetching || blockCreators.isPending}
                    onClick={async () => {
                      const openIds = [...selected]
                        .map((id) => selectedCreatorsData.get(id)?.tiktokOpenId)
                        .filter((id): id is string => id != null);
                      if (openIds.length === 0) return;
                      try {
                        const blocked = await blockCreators.block(openIds);
                        if (blocked !== null) {
                          toastSuccess(
                            `${blocked} Creator${blocked === 1 ? "" : "s"} blocked. Unblock anytime from Operations/Blocked Creators.`,
                          );
                        }
                        handleClearSelection();
                      } catch {
                        toastError("Failed to block creators");
                      }
                    }}
                  >
                    Block
                  </Button>
                </div>
              </div>
            )}

            <FilterPills className="-my-2" extraPills={tablePills} />

            <CreatorExplorerTable
              rows={tableData}
              dateRange={dateRange}
              creatorTagsEnabled={creatorTagsEnabled}
              onClickRow={(row) => {
                setSelectedCreatorId(row.creatorId);
                navigate({ search: (prev) => ({ ...prev, creator_id: row.creatorId }) });
              }}
              selection={{ raw: true, selectedKeys: new Set(selected), onSelectionChange: handleSelectionChange }}
              sorting={{ descriptor, onSortChange: setFromDescriptor }}
              onHiddenSortColumn={sortByName}
              pagination={{
                page: pagination.page,
                totalPages: Math.ceil((total ?? 0) / pagination.pagination.limit),
                onPageChange: pagination.setPage,
              }}
              hasActiveFilters={hasActiveFilterFields || !!debouncedSearch || isApplied}
              onClearFilters={resetFilters}
              onManageAllTags={() => setTagLibraryOpen(true)}
              onManageAllCohorts={() => setCohortLibraryOpen(true)}
            />
            <CreatorDetails
              id={selectedCreatorId}
              onClose={() => {
                setSelectedCreatorId(undefined);
                navigate({ search: (prev) => ({ ...prev, creator_id: undefined }) });
              }}
            />
            <VideoDetails
              isAssurance={isAssurance}
              id={selectedVideoId}
              onClose={() => setSelectedVideoId(undefined)}
            />
          </>
        )}
      </div>
      <CreatorFilterPanel
        open={filtersOpen}
        close={() => setFiltersOpen(false)}
        clearAll={() => resetFilters()}
        dateRange={dateRange}
      />

      {/* Invite to Collaboration Modal. Creators with no TikTok open id can't be invited at all
            (TikTok's CREATE only accepts open ids), so we filter them out here rather than failing
            them per-row in the worker. */}
      <InviteToCollaborationModal
        creators={Array.from(selectedCreatorsData.values())
          .filter((c): c is CreatorData & { tiktokOpenId: string } => Boolean(c.tiktokOpenId))
          .map((c) => ({
            openId: c.tiktokOpenId,
            username: c.creatorName ?? c.handle ?? "",
            nickname: c.nickname ?? c.creatorName,
            avatar: c.thumbnail,
          }))}
        open={bulkInviteModalOpen}
        onOpenChange={setBulkInviteModalOpen}
        onSuccess={handleClearSelection}
      />

      {/* Bulk Remove from Collaboration Modal - commented out - pending Samples API integration
        {(
          <BulkRemoveCreatorFromCollabModal
            creatorIds={Array.from(selected)}
            open={bulkRemoveModalOpen}
            onOpenChange={setBulkRemoveModalOpen}
            onSuccess={handleClearSelection}
          />
        )}
        */}

      {/* Bulk add to lists — fallback while the creator-tags FF is off */}
      <AddtoListsModal
        selectedCreators={selected}
        onAddToList={handleClearSelection}
        open={addToListsBulkOpen}
        onOpenChange={setAddToListsBulkOpen}
      />
    </PageLayout>
  );
};

const ACTIVE_FIELDS = [
  // GMV (top level)
  "minGmv",
  "maxGmv",
  // Collaboration Type (top level)
  "collaborationType",
  // Performance section
  "minCtr",
  "maxCtr",
  "minCtor",
  "maxCtor",
  "minOrders",
  "maxOrders",
  "minRevenue",
  "maxRevenue",
  "minAov",
  "maxAov",
  // Safety Risks section
  "videoRiskStatus",
  "minVideosAtRiskPercent",
  "maxVideosAtRiskPercent",
  "riskTier",
  "riskId",
  // Creator Overview section
  "creatorType",
  "minFollowers",
  "maxFollowers",
  "topSellingCategories",
  "minTotalLikes",
  "maxTotalLikes",
  "minPostingFrequency",
  "maxPostingFrequency",
  "creatorLevel",
  "categoryPro",
  "creatorLanguage",
  // Creator Attributes section
  "toneAndApproach",
  "contentFocus",
  "productionQuality",
  // Audience section
  "maleAudiencePercent",
  "femaleAudiencePercent",
  "age",
  // Featured Products section
  "product",
  // Tags section
  "includeTags",
  "excludeTags",
  // Cohorts section
  "includeCohorts",
  "excludeCohorts",
  // Lists section
  "creatorList",
  // Ad Campaigns section
  "campaign",
  "postedFrom",
  "postedTo",
] as const;

export const CreatorExplorer = () => {
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof creatorExplorerRoute)["useSearch"]>;

  return (
    <FiltersProvider activeFields={[...ACTIVE_FIELDS]} initialValues={urlParams}>
      <CreatorExplorerContent />
    </FiltersProvider>
  );
};
